import React, { FC, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import Form from '../../components/form/form';
import StudentSelector from '../../components/student-selector/student-selector';
import SubmitButton from '../../components/submit-button/submit-button';
import Loading from '../../components/loading/loading';
import ErrorComponent from '../../components/error/error';

// Form Data
import { formDataNoticeBoardCreate } from '../../form-data/form-data-notice-board-create';

// Helpers
import {
  getNoticeBoardData,
  handleFormOnChange,
  checkIfAllFormHasFilled,
  forceRemovePreviewImage,
  handleStudentChange,
  deleteAttachment,
  deleteNoticeBoard,
} from '../../helpers/notice-board-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ClassroomService from '../../services/classroom-service';
import NoticeBoardService from '../../services/notice-board-service';

const NoticeBoardEdit: FC = () => {
  const navigate = useNavigate();
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const classroomService = new ClassroomService();
  const [error, setError] = useState(false);
  const [data, setData] = useState<NoticeBoardFormData>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isLoadingAttachment, setIsLoadingAttachment] = useState(false);

  const { noticeBoardId } = useParams<{ noticeBoardId: string }>();
  const noticeBoardService = new NoticeBoardService();

  const [noticeBoard, setNoticeBoard] = useState<NoticeBoardCreated>(null);
  useEffect(() => {
    noticeBoardService
      .getNoticeBoard(noticeBoardId)
      .then((result) => {
        const noticeBoardData = getNoticeBoardData(result);
        setData(noticeBoardData);
        setNoticeBoard(result);
      })
      .catch(() => setError(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const [validations, setValidations] = useState({});
  const [allowSubmit, setAllowSubmit] = useState(true);
  useEffect(() => {
    checkIfAllFormHasFilled(data, validations, allowSubmit, setAllowSubmit);
  }, [validations, allowSubmit, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [formElements, setFormElements] = useState<FormParams>(null);
  const [classroomList, setClassroomList] = useState<
    ClassroomStudents[] | null
  >(null);
  useEffect(() => {
    classroomService
      .listClassroomStudents()
      .then((classroomsResponse) => {
        if (!noticeBoard) {
          return;
        }

        const selectedClassrooms = noticeBoard.classroomIds;

        handleStudentChange(
          selectedClassrooms,
          [],
          data,
          setData
        );
        setClassroomList(classroomsResponse);
        setFormElements(formDataNoticeBoardCreate(noticeBoard));
      })
      .catch((err) => {
        console.error(err);
        return setError(true);
      });
  }, [noticeBoard]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateNoticeBoardPromise = (submitData: NoticeBoardFormData) => {
    const noticeBoardService = new NoticeBoardService();

    return noticeBoardService
      .updateNoticeBoard(submitData, noticeBoardId)
      .then(() => {
        setToastContent({
          isOpen: true,
          text: 'Mural salvo!',
          type: 'success',
        });
        navigate('/mural/listar');
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao salvar o mural.',
          type: 'error',
        });
      });
  };

  const handleSubmit = () => {
    const submitData = Object.assign({}, data);

    updateNoticeBoardPromise(submitData);
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (!formElements || !classroomList) return <Loading type='contentFull' />;

  return (
    <BaseContentContainer
      biggerSide={BaseContentContainerBiggerSide.right}
      format={BaseContentContainerFormat.doubleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <Form
        formData={formElements}
        validations={validations}
        handleOnChange={(evt) =>
          handleFormOnChange(
            evt,
            validations,
            data,
            setValidations,
            setData,
            setIsLoadingImage,
            setIsLoadingAttachment
          )
        }
        previewImage={data.formData['image'] ? data.formData['image'] : null}
        forceRemovePreviewImage={(id: string) =>
          forceRemovePreviewImage(id, data, setData)
        }
        isLoadingImage={isLoadingImage}
        attachments={data.formData.attachment ? [data.formData.attachment] : []}
        isLoadingAttachment={isLoadingAttachment}
        deleteAttachment={(id: number) => deleteAttachment(id, data, setData, validations, setValidations)}
      />
      {classroomList && (
        <StudentSelector
          classrooms={classroomList}
          handleStudentChange={(
            selectedClassrooms: number[],
            selectedStudents: number[]
          ) =>
            handleStudentChange(
              selectedClassrooms,
              selectedStudents,
              data,
              setData
            )
          }
          initialClassrooms={data.classData}
          initialStudents={[]}
          hideStudentSelection={true}
        />
      )}
      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <Link to='/mural/listar'>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value='deletar'
          variant='terciary'
          onClick={() =>
            deleteNoticeBoard(
              noticeBoardId,
              setModalContent,
              setToastContent,
              navigate
            )
          }
        />
        <SubmitButton
          value='salvar'
          variant='primary'
          onClick={handleSubmit}
          disabled={!allowSubmit}
        />
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default NoticeBoardEdit;
